import { useEffect, useMemo, useState } from "react";
import { useForm } from "@tanstack/react-form";
import {
  RadioGroup,
  RadioGroupOption,
  RadioGroupContainer,
} from "../../../../components/radioSelector/RadioGroup";
import { useSellarDistributionContactList } from "../../../../hooks/api/v2/sellarDistributionContact/useSellarDistributionContactList";
import { InfiniteList } from "../../../../components/infiniteLoading/InfiniteList";
import { Button, InputContainer, InputTel } from "@sellar-io/components";
import { z } from "zod";
import { InputLabel } from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import { useSellarDistributionContactCreate } from "../../../../hooks/api/v2/sellarDistributionContact/useSellarDistributionContactCreate";
import { twMerge } from "tailwind-merge";
import { useSellarUser } from "../../../../hooks";
import { useCurrentBusinessUser } from "../../../../hooks/api/v2/business/useCurrentBusinessUser";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";

export const SellarDistributionContactCard = ({
  onContactUpdate,
  contactId,
  className,
  checkedClassName,
  checkedRadioClassName,
}: {
  onContactUpdate: (id?: number) => void;
  contactId?: number;
  className?: string;
  checkedClassName?: string;
  checkedRadioClassName?: string;
}) => {
  const { user } = useSellarUser();
  const { business } = useSellarCurrentBusiness();
  const { data: businessUser } = useCurrentBusinessUser(business?.id);
  const [selectedContactId, setSelectedContactId] = useState<
    number | undefined
  >(contactId);
  const sellarDistributionContactsQuery = useSellarDistributionContactList({
    limit: 5,
  });

  const sellarDistributionContacts = useMemo(
    () => sellarDistributionContactsQuery.data?.pages.flat() ?? [],
    [sellarDistributionContactsQuery.data],
  );

  const sellarDistributionContactCreate = useSellarDistributionContactCreate();

  const defaultContactId = useMemo(() => {
    const currentUserContact = sellarDistributionContacts.find(
      (contact) => contact.BusinessUserId === businessUser?.id,
    );
    return currentUserContact?.id ?? sellarDistributionContacts[0]?.id ?? -1;
  }, [sellarDistributionContacts, businessUser]);

  const effectiveContactId = selectedContactId ?? defaultContactId;

  const hasContactDetails = effectiveContactId > 0;

  useEffect(() => {
    onContactUpdate(effectiveContactId > 0 ? effectiveContactId : undefined);
  }, [effectiveContactId, onContactUpdate]);

  return (
    <InfiniteList query={sellarDistributionContactsQuery}>
      <RadioGroup
        value={effectiveContactId.toString()}
        onChange={(value) => setSelectedContactId(parseInt(value, 10))}
      >
        <RadioGroupContainer className={twMerge("bg-white", className)}>
          {!hasContactDetails && businessUser && (
            <RadioGroupOption
              value={"-1"}
              checkedClassName={checkedClassName}
              checkedRadioClassName={checkedRadioClassName}
              radioClassName={twMerge(
                effectiveContactId === -1 && "mb-[155px]",
              )}
            >
              <div className="flex flex-col gap-4">
                <span className="font-semibold">Use my contact details</span>
                {effectiveContactId === -1 && (
                  <ContactForm
                    contact={{
                      name: user?.name ?? "",
                      email: user?.email ?? "",
                      phoneNumber: user?.smsNumber ?? "",
                    }}
                    loading={sellarDistributionContactCreate.isPending}
                    onSave={(contact) => {
                      sellarDistributionContactCreate.mutate(
                        { ...contact, BusinessUserId: businessUser.id },
                        {
                          onSuccess: (response) => {
                            setSelectedContactId(response.id);
                          },
                        },
                      );
                    }}
                  />
                )}
              </div>
            </RadioGroupOption>
          )}
          {sellarDistributionContacts.map((contact) => (
            <RadioGroupOption
              key={contact.id}
              value={contact.id.toString()}
              checkedClassName={checkedClassName}
              checkedRadioClassName={checkedRadioClassName}
            >
              <p>
                {contact.BusinessUserId === businessUser?.id ? (
                  <span className="font-semibold">Use my contact details</span>
                ) : (
                  <span className="font-semibold">
                    Use {contact.name} as delivery contact
                  </span>
                )}
                <br />
                {contact.phoneNumber}
                {contact.email && " • "}
                {contact.email}
              </p>
            </RadioGroupOption>
          ))}
          <RadioGroupOption
            value={"0"}
            checkedClassName={checkedClassName}
            checkedRadioClassName={checkedRadioClassName}
            radioClassName={twMerge(effectiveContactId === 0 && "mb-[155px]")}
          >
            <div className="flex flex-col gap-4">
              <span className="font-semibold">
                Use a different delivery contact
              </span>
              {effectiveContactId === 0 && (
                <ContactForm
                  loading={sellarDistributionContactCreate.isPending}
                  onSave={(contact) => {
                    sellarDistributionContactCreate.mutate(
                      { ...contact, BusinessUserId: null },
                      {
                        onSuccess: (response) => {
                          setSelectedContactId(response.id);
                        },
                      },
                    );
                  }}
                />
              )}
            </div>
          </RadioGroupOption>
        </RadioGroupContainer>
      </RadioGroup>
    </InfiniteList>
  );
};

const formSchema = z.object({
  name: z.string(),
  email: z.string().nullable(),
  phoneNumber: z.string(),
});

type FormType = z.infer<typeof formSchema>;

const ContactForm = ({
  contact,
  loading,
  onSave,
}: {
  contact?: FormType;
  loading: boolean;
  onSave: (contact: FormType) => void;
}) => {
  const form = useForm({
    defaultValues: {
      name: contact?.name ?? "",
      email: contact?.email ?? "",
      phoneNumber: contact?.phoneNumber ?? "",
    },
    onSubmit: ({ value }) => {
      onSave(formSchema.parse(value));
    },
  });

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        form.handleSubmit();
      }}
      className="flex w-full flex-col gap-4"
    >
      <div className="flex w-full flex-col gap-2 md:flex-row!">
        <form.Field name="name">
          {({ state, handleBlur, handleChange }) => {
            return (
              <div className="flex w-full flex-col gap-1">
                <InputLabel>*Name</InputLabel>
                <InputContainer>
                  <InputText
                    onKeyDown={(e) => e.stopPropagation()}
                    defaultValue={state.value}
                    onBlur={handleBlur}
                    onChange={(e) => handleChange(e.target.value)}
                  />
                </InputContainer>
              </div>
            );
          }}
        </form.Field>
        <form.Field
          name="phoneNumber"
          validators={{
            onChange: ({ value }) =>
              value.length === 0 ? "Phone number can't be blank" : undefined,
          }}
        >
          {({ state, handleBlur, handleChange }) => {
            return (
              <div className="flex w-full flex-col gap-1">
                <InputLabel>*Phone number</InputLabel>
                <InputContainer>
                  <InputTel
                    defaultValue={state.value}
                    onBlur={handleBlur}
                    onChange={(e) => handleChange(e.target.value)}
                    placeholder=""
                  />
                </InputContainer>
                {!state.meta.isValid && (
                  <span role="alert" className="text-xs text-red-500">
                    {state.meta.errors.join(", ")}
                  </span>
                )}
              </div>
            );
          }}
        </form.Field>
        <form.Field name="email">
          {({ state, handleBlur, handleChange }) => {
            return (
              <div className="flex w-full flex-col gap-1">
                <InputLabel>Email</InputLabel>
                <InputContainer>
                  <InputText
                    defaultValue={state.value}
                    onBlur={handleBlur}
                    onChange={(e) => handleChange(e.target.value)}
                  />
                </InputContainer>
              </div>
            );
          }}
        </form.Field>
      </div>
      <div className="flex gap-2">
        <Button type="submit" loading={loading}>
          Save
        </Button>
      </div>
    </form>
  );
};
